import { getPwaWorkspaceConfig } from '../config/workspace-config';

export interface ApiResponse<T = any> {
  success: boolean;
  data: T | null;
  message: string | null;
  code?: string;
}

const DEFAULT_WORKSPACE_ID = getPwaWorkspaceConfig().workspaceId;

const getApiBaseUrl = (): string => {
  // Production: explicit API URL env var
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
    const rawUrl = import.meta.env.VITE_API_URL.replace(/\/$/, '');
    if (rawUrl.endsWith('/api/v1')) return rawUrl;
    return `${rawUrl}/api/v1`;
  }
  // Always use a relative path so requests go through the Vite dev proxy (avoids CORS),
  // and through the reverse-proxy/same-origin in production deployments.
  return '/api/v1';
};

class PwaApiClient {
  private token: string | null = null;
  private workspaceId: string = DEFAULT_WORKSPACE_ID;

  constructor() {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) this.token = savedToken;

      const hasEnvWorkspace =
        typeof import.meta !== 'undefined' &&
        import.meta.env &&
        (import.meta.env.VITE_WORKSPACE_ID || import.meta.env.VITE_WORKSPACE);

      if (!hasEnvWorkspace) {
        const savedWs = localStorage.getItem('workspace_id');
        if (savedWs) this.workspaceId = savedWs;
      }
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (typeof window !== 'undefined' && window.localStorage) {
      if (token) {
        localStorage.setItem('auth_token', token);
      } else {
        localStorage.removeItem('auth_token');
      }
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setWorkspaceId(id: string) {
    this.workspaceId = id;
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('workspace_id', id);
    }
  }

  getWorkspaceId(): string {
    return this.workspaceId;
  }

  private async request<T = any>(
    path: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers = new Headers(options.headers || {});
    headers.set('Content-Type', 'application/json');
    headers.set('X-Workspace-Id', this.workspaceId);
    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${path}`, {
        ...options,
        headers,
        cache: 'no-store',
      });

      let json: any;
      try {
        json = await response.json();
      } catch (err) {
        json = {
          success: false,
          data: null,
          message: 'Failed to parse server response',
          code: 'PARSE_ERROR',
        };
      }

      if (!response.ok) {
        throw {
          statusCode: response.status,
          success: false,
          data: json.data,
          message: json.message || 'An error occurred on the server',
          code: json.code || 'UNKNOWN_ERROR',
        };
      }

      return json;
    } catch (err: any) {
      if (err.statusCode) throw err;
      throw {
        success: false,
        data: null,
        message: err.message || 'Network request failed. Ensure backend server is running.',
        code: 'NETWORK_ERROR',
      };
    }
  }

  auth = {
    signup: async (name: string, email: string, phone: string, password: string, referralCode?: string) => {
      return this.request<{ phone: string; otpCode: string }>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ name, email, phone, password, referralCode, workspaceId: this.workspaceId }),
      });
    },

    verifyOtp: async (phone: string, code: string) => {
      const res = await this.request<{ token: string; user: any }>('/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ phone, code }),
      });
      if (res.success && res.data?.token) {
        this.setToken(res.data.token);
      }
      return res;
    },

    login: async (email: string, password: string) => {
      const res = await this.request<{ token: string; user: any }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (res.success && res.data?.token) {
        this.setToken(res.data.token);
      }
      return res;
    },

    logout: async () => {
      try {
        await this.request('/auth/logout', { method: 'POST' });
      } finally {
        this.setToken(null);
      }
    }
  };

  games = {
    list: async () => {
      return this.request<any[]>('/games');
    }
  };

  promotions = {
    list: async () => {
      return this.request<any[]>('/promotions');
    }
  };

  payments = {
    list: async () => {
      return this.request<any[]>('/payment-methods');
    }
  };

  user = {
    getProfile: async () => {
      return this.request<any>('/users/me/profile');
    },
    updateProfile: async (data: { name?: string; password?: string; profileImage?: string | null }) => {
      return this.request<any>('/users/me/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    }
  };

  chat = {
    getConversation: async () => {
      return this.request<any>('/chat/my-conversation');
    },

    sendMessage: async (conversationId: string, text: string, imageUrl: string | null = null) => {
      return this.request<any>(`/chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text, imageUrl }),
      });
    },

    markAsRead: async (conversationId: string) => {
      return this.request<any>(`/chat/conversations/${conversationId}/read`, {
        method: 'POST',
      });
    }
  };

  upload = {
    image: async (formData: FormData, endpoint?: string) => {
      const headers = new Headers();
      if (this.token) {
        headers.set('Authorization', `Bearer ${this.token}`);
      }
      headers.set('X-Workspace-Id', this.workspaceId);
      
      const baseUrl = getApiBaseUrl();
      const url = endpoint ? `${baseUrl}/upload/${endpoint}` : `${baseUrl}/upload`;
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error((json as any).message || 'Image upload failed');
      }
      
      return response.json();
    }
  };

  notifications = {
    list: async (limit = 20, offset = 0) => {
      return this.request<any[]>(`/notifications?limit=${limit}&offset=${offset}`);
    },
    markAsRead: async (id: string) => {
      return this.request<any>(`/notifications/${id}/read`, {
        method: 'PUT',
      });
    },
    markAllAsRead: async () => {
      return this.request<any>(`/notifications/read-all`, {
        method: 'PUT',
      });
    },
    registerDevice: async (data: { deviceId: string; fcmToken: string; platform: string; appVersion?: string; appInstanceId: string }) => {
      return this.request<any>(`/notifications/devices`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    unregisterDevice: async (deviceId: string, appInstanceId: string) => {
      return this.request<any>(`/notifications/devices/${deviceId}`, {
        method: 'DELETE',
        body: JSON.stringify({ appInstanceId }),
      });
    },
    getPreferences: async () => {
      return this.request<any[]>(`/notifications/preferences`);
    },
    updatePreference: async (data: { category: string; channel: string; enabled: boolean }) => {
      return this.request<any>(`/notifications/preferences`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    }
  };
}

export const api = new PwaApiClient();
